import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { mapApiError } from "../../lib/api";
import { useSession } from "../auth/hooks";

import { type FlagReason, flagReport } from "./api";

const REASON_ORDER: FlagReason[] = ["spam", "incorrect", "inappropriate"];

const REASON_KEYS: Record<FlagReason, string> = {
  spam: "reports.reason_spam",
  incorrect: "reports.reason_incorrect",
  inappropriate: "reports.reason_inappropriate",
};

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "rate-limited"; retryAfter: number }
  | { kind: "spam-detected" }
  | { kind: "validation" }
  | { kind: "unauthorized" }
  | { kind: "network" }
  | { kind: "server" }
  | { kind: "error"; message: string };

type Props = {
  reportId: string;
  onClose: () => void;
};

export function FlagDialog({ reportId, onClose }: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<FlagReason>("spam");
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const { user } = useSession();

  // Tick down the rate-limit retry timer once per second so the banner
  // doesn't read a frozen "N s" — N decrements toward 0 in the user's view.
  useEffect(() => {
    if (status.kind !== "rate-limited" || status.retryAfter <= 0) return;
    const id = setInterval(() => {
      setStatus((prev) => {
        if (prev.kind !== "rate-limited") return prev;
        const next = prev.retryAfter - 1;
        return next <= 0 ? { kind: "idle" } : { kind: "rate-limited", retryAfter: next };
      });
    }, 1000);
    return () => clearInterval(id);
  }, [status]);

  async function submit() {
    setStatus({ kind: "submitting" });
    try {
      await flagReport(reportId, reason, { authed: !!user });
      setStatus({ kind: "success" });
    } catch (err) {
      setStatus(mapApiError(err) as Status);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="flag-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-sm rounded-lg bg-surface p-4 shadow-lg">
        <h2 id="flag-title" className="text-lg font-semibold text-fg">
          {t("reports.list_title")}
        </h2>
        <p className="mt-1 text-sm text-fg-mute">{t("reports.list_body")}</p>

        <fieldset className="mt-3 space-y-2">
          {REASON_ORDER.map((value) => (
            <label key={value} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="reason"
                value={value}
                checked={reason === value}
                onChange={() => setReason(value)}
              />
              {t(REASON_KEYS[value])}
            </label>
          ))}
        </fieldset>

        <StatusBanner status={status} />

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-fg-mute hover:bg-surface-raised"
          >
            {status.kind === "success" ? t("common.close") : t("common.cancel")}
          </button>
          {status.kind !== "success" && (
            <button
              type="button"
              onClick={() => void submit()}
              disabled={status.kind === "submitting"}
              className="rounded-md bg-ember px-3 py-1.5 text-sm font-medium text-fg hover:bg-ember-deep disabled:cursor-not-allowed disabled:opacity-50"
            >
              {status.kind === "submitting" ? t("reports.submit_sending") : t("common.report")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBanner({ status }: { status: Status }) {
  const { t } = useTranslation();
  if (status.kind === "success") {
    return <p role="status" className="mt-3 text-sm text-volt-deep">{t("reports.flag_success")}</p>;
  }
  if (status.kind === "rate-limited") {
    return (
      <p role="alert" className="mt-3 text-sm text-ember">
        {t("errors.rate_limited_generic", { seconds: status.retryAfter })}
      </p>
    );
  }
  if (status.kind === "spam-detected") {
    return <p role="alert" className="mt-3 text-sm text-ember">{t("errors.spam_detected")}</p>;
  }
  if (status.kind === "validation") {
    return <p role="alert" className="mt-3 text-sm text-ember">{t("errors.validation_minimal")}</p>;
  }
  if (status.kind === "unauthorized") {
    return <p role="alert" className="mt-3 text-sm text-ember">{t("errors.unauthorized")}</p>;
  }
  if (status.kind === "network") {
    return <p role="alert" className="mt-3 text-sm text-ember">{t("errors.network")}</p>;
  }
  if (status.kind === "server") {
    return <p role="alert" className="mt-3 text-sm text-ember">{t("errors.server")}</p>;
  }
  if (status.kind === "error") {
    return (
      <p role="alert" className="mt-3 text-sm text-ember">
        {t("reports.send_failed_with_reason", { reason: status.message || t("errors.unknown") })}
      </p>
    );
  }
  return null;
}

